import Card from "@material-ui/core/Card";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Settings from "@material-ui/icons/Settings";
import classnames from "classnames";
import React, { useContext } from "react";
import { Loading } from "../components/Loading";
import { MetricReport, Query } from "../lib/api/charts";
import red from "@material-ui/core/colors/red";
import orange from "@material-ui/core/colors/orange";
import { history } from "../lib/history";
import { Route } from "react-router-dom";
import { GroupMetricContext } from "../contexts/GroupMetricContext";
import { FormattedMessage, useIntl } from "react-intl";
import { DashboardConfigContext } from "../contexts/DashboardConfigContext";
import Tooltip from "@material-ui/core/Tooltip";

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: "#f8f8f8",
      borderRadius: "5px",
      borderRight: "solid 3px #f3f3f3",
      borderBottom: "solid 3px #f3f3f3",
      borderLeft: "solid 3px #f3f3f3",
      borderTop: "solid 3px #f3f3f3",
      padding: theme.spacing.unit,
      paddingBottom: "10px !important",
      color: "#555",
      height: "100%",
    },
    cardDriverContent: {
      display: "grid",
      gridTemplateAreas: `
      "content"
    `,
      gridGap: `${theme.spacing.unit}px`,
      gridTemplateColumns: "auto",
      color: "#555",
    },
    cardHeader: {
      color: "#555",
      fontWeight: 600,
      paddingBottom: theme.spacing.unit / 2,
    },
    dealerTable: {
      paddingTop: "4px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridColumnGap: "10px",
    },
    benchmarkGroup: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      paddingBottom: theme.spacing.unit / 2,
      gridColumnGap: "10px",
    },
    yellow: {
      color: `${orange[500]} !important`,
    },
    red: {
      color: `${red[500]} !important`,
    },
    black: {
      color: "#555",
    },
    bold: {
      fontWeight: 600,
    },
    dealerInfo: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridGap: "5px",
    },
    benchmarkText: {
      display: "flex",
      justifyContent: "space-between",
    },
    cardText: {
      lineHeight: "1.8em !important",
      fontSize: "12px",
      color: "#555",
    },
    cardValue: {
      lineHeight: "1.8em !important",
      fontSize: "12px",
      color: "#555",
      textAlign: "right",
    },
    settings: {
      fill: "#636463",
      fontSize: "24px !important",
      "&:hover": {
        cursor: "pointer",
      },
    },
    actions: {
      display: "none",
      flexDirection: "row",
      alignItems: "center",
      [theme.breakpoints.up("sm")]: {
        display: "block",
      },
      [theme.breakpoints.up("md")]: {
        display: "block",
      },
      [theme.breakpoints.up("lg")]: {
        display: "block",
      },
      [theme.breakpoints.up("xl")]: {
        display: "block",
      },
    },
    metricCardHeader: {
      display: "flex",
      height: "55px",
      justifyContent: "space-between",
      fontWeight: "bold",
    },
    hideCard: {
      display: "none",
      [theme.breakpoints.up("lg")]: {
        display: "block",
      },
      [theme.breakpoints.up("xl")]: {
        display: "block",
      },
    },
    title: {
      color: "#636463",
      fontWeight: 600,
      fontSize: `${theme.typography.fontSize + 3}px`,
      textDecoration: "none",
    },
    headingChildren: {
      fontWeight: "bold",
      fontSize: `${theme.typography.fontSize + 6}px`,
    },
    subheading: {
      fontSize: `${theme.typography.fontSize + 3}px`,
    },
    toolTipNoMaxWidth: {
      maxWidth: "none",
    },
  });

export type MetricProps = {
  query: Query;
  title?: string;
  metric: MetricReport;
  renderIcon?(): React.ReactNode;
  driverMetric?: string;
  allowFlip?: boolean;
} & React.HtmlHTMLAttributes<HTMLDivElement> &
  WithStyles<typeof styles>;

const metricColour = (metric: any, classes: any, metricValue: any) => {
  if (metric.classification == "income") {
    return {
      [classes.yellow]: metricValue.actual.value >= metric.median.value && metricValue.actual.value < metric.benchmark.value,
      [classes.red]: metricValue.actual.value < metric.median.value,
    };
  }
  return {
    [classes.yellow]: metricValue.actual.value < metric.median.value && metricValue.actual.value > metric.benchmark.value,
    [classes.red]: metricValue.actual.value > metric.median.value,
  };
};

const replaceNonNumbers = (value: any) => {
  if ((value || "").includes("NaN") || (value || "").includes("Infinity")) {
    return "";
  }
  return value;
};

export type MetricHeaderProps = {
  title: string;
  driverMetric?: string;
} & React.HtmlHTMLAttributes<HTMLDivElement> &
  WithStyles<typeof styles>;

const MetricHeader: React.FunctionComponent<MetricHeaderProps> = ({ classes, title, driverMetric, children }) => {
  const { dashboardTypeConfig } = useContext(DashboardConfigContext);
  return (
    <div className={classnames(classes.title, classes.bold)}>
      {title.includes("-") ? (
        <span>
          <Typography className={classnames(classes.bold, classes.headingChildren)} variant="h6" color="inherit">
            {title.split(" - ")[0]}
          </Typography>
          <Typography className={classnames(classes.bold, classes.headingChildren, classes.subheading)} variant="h6" color="inherit">
            {title.split(" - ")[1]}
          </Typography>
        </span>
      ) : (
        <span>
          <Typography className={classnames(classes.bold, classes.headingChildren)} variant="h6" color="inherit">
            {title}
          </Typography>
          {dashboardTypeConfig.value == "mra_drivers" && driverMetric && (
            <Typography className={classnames(classes.bold, classes.headingChildren, classes.subheading)} variant="h6" color="inherit">
              {driverMetric}
            </Typography>
          )}
        </span>
      )}
      {children}
    </div>
  );
};

const GroupMetricUnstlyed: React.FunctionComponent<MetricProps> = ({
  classes,
  title,
  metric: report,
  query,
  renderIcon,
  driverMetric = undefined,
  allowFlip = true,
}) => {
  const intl = useIntl();
  const { getGroupMetric } = useContext(GroupMetricContext);
  const { metric, loading } = getGroupMetric(report, query);

  if (!loading.loaded || loading.loading) {
    return <Loading />;
  }

  const metricTitle = title || metric.title;

  const benchmarksType = query.benchmarks
    .filter(bm => bm.length > 0)
    .map(x => {
      const [bench, part] = x.split(/-(?=[^-]+$)/);
      switch ((part || "").trim()) {
        case "MD":
          return intl.formatMessage({ id: "metric.median.label" });
        case "BM":
          return intl.formatMessage({ id: "metric.benchmark.label" });
        case "AV":
          return intl.formatMessage({ id: "metric.average.label" });
        case "UL":
          return intl.formatMessage({ id: "metric.upper.limit.label" });
        case "LL":
          return intl.formatMessage({ id: "metric.lower.limit.label" });
        default:
          return intl.formatMessage({ id: "metric.benchmark.label" });
      }
    });

  return (
    <Card className={classnames(classes.root, metricTitle == "N/A" && classes.hideCard)}>
      <div className={classes.metricCardHeader}>
        <FormattedMessage id={metricTitle}>{text => <MetricHeader title={text.toString()} driverMetric={driverMetric} classes={classes} />}</FormattedMessage>
        <div className={classes.actions}>
          {metricTitle != "N/A" && allowFlip && (
            <Route
              path="/group-dashboard/:dashboard"
              render={({ match }) => (
                <Settings
                  onClick={() => history.push(`/group-dashboard/${match.params.dashboard}/insight/${report}/${query.department}`)}
                  className={classes.settings}
                />
              )}
            />
          )}
        </div>
      </div>
      <div className={classes.benchmarkGroup}>
        {benchmarksType.length > 0 && (
          <>
            <Typography className={classes.benchmarkText}>
              <span className={classnames(classes.cardText)}>{benchmarksType[0]}</span>
              <span className={classnames(classes.bold, classes.cardText)}>{metric && metric.benchmark.formatted}</span>
            </Typography>
            {benchmarksType.length > 1 && (
              <Typography className={classes.benchmarkText}>
                <span className={classnames(classes.cardText)}>{benchmarksType[1]}</span>
                <span className={classnames(classes.bold, classes.cardText)}>{metric && metric.median.formatted}</span>
              </Typography>
            )}
          </>
        )}
      </div>
      <div className={classes.dealerTable}>
        {((metric && metric.values) || []).map((value, key: number) => {
          if (key < 5) {
            return (
              <div key={`${value.dealer}_${key}`} className={classes.dealerInfo}>
                <Tooltip classes={{ tooltip: classes.toolTipNoMaxWidth }} title={<h1 style={{ whiteSpace: "nowrap" }}>{value.dealerNames}</h1>}>
                  <Typography className={classnames(classes.cardText)}>{value.dealer}</Typography>
                </Tooltip>
                {value.actual.value != 0 ? (
                  <Tooltip
                    title={
                      <>
                        <h1>Y-1: &nbsp; {replaceNonNumbers(value.previous.formatted)}</h1>
                        <h1>Var: &nbsp; {replaceNonNumbers(value.variance.formatted)}</h1>
                      </>
                    }
                  >
                    <Typography className={classnames(classes.cardValue, metricColour(metric, classes, value), classes.bold)}>
                      {replaceNonNumbers(value.actual.formatted)}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography className={classnames(classes.cardValue, classes.bold, classes.black)}>-</Typography>
                )}
              </div>
            );
          }
          return (
            <div key={`${value.dealer}_${key}`} className={classes.dealerInfo}>
              <Tooltip classes={{ tooltip: classes.toolTipNoMaxWidth }} title={<h1 style={{ whiteSpace: "nowrap" }}>{value.dealerNames}</h1>}>
                <Typography className={classnames(classes.cardText)}>{value.dealer}</Typography>
              </Tooltip>
              {value.actual.value != 0 ? (
                <Tooltip
                  title={
                    <>
                      <h1>Y-1: &nbsp; {replaceNonNumbers(value.previous.formatted)}</h1>
                      <h1>Var: &nbsp; {replaceNonNumbers(value.variance.formatted)}</h1>
                    </>
                  }
                >
                  <Typography className={classnames(classes.cardValue, metricColour(metric, classes, value), classes.bold)}>
                    {replaceNonNumbers(value.actual.formatted)}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography className={classnames(classes.cardValue, classes.bold, classes.black)}>-</Typography>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const GroupMetricCard = withStyles(styles)(GroupMetricUnstlyed);
